// Client-side providers for every route:
// redux + persist, auth, toaster, scroll-to-top, and the auth bootstrap loader.
import "../polyfills";
import { useEffect } from "react";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { store } from "redux/store";
import AuthProvider from "hoc/AuthProvider";
import { useAuth } from "context/UseContext";
import { ToasterContainer } from "components/Toaster";
import { CircleLoading } from "components/Loaders";
import ScrollToTop from "hooks/ScrollToTop";
import TenantGate from "./TenantGate";

const persistor = persistStore(store);

function AuthGate({ children }) {
  const { isAuthBootstrapping } = useAuth();
  if (isAuthBootstrapping) return <CircleLoading />;
  return children;
}

export default function ClientRoot({ children }) {
  useEffect(() => {
    // Push notifications: only when Firebase is configured (REACT_APP_FIREBASE_*).
    const firebase = Object.fromEntries(
      Object.entries({
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
      }).filter(([, v]) => v),
    );
    if (firebase.apiKey && "serviceWorker" in navigator) {
      navigator.serviceWorker.register(`/firebase-messaging-sw.js?${new URLSearchParams(firebase)}`).catch((err) => {
        console.log("Service worker registration failed, error:", err);
      });
    }
  }, []);

  return (
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <AuthProvider>
          <ToasterContainer />
          <TenantGate>
            <AuthGate>{children}</AuthGate>
          </TenantGate>
          <ScrollToTop />
        </AuthProvider>
      </PersistGate>
    </Provider>
  );
}
